import { Eye, Users } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatDate, formatRelative, initialsFromName } from "@/lib/utils";
import {
  useAssetViews,
  type AssetViewAnalyticsResponse,
} from "./use-asset-views";

// Phase 7 sub-bucket 13. Asset detail Views tab. Three sections:
//   - Hero cards: total views + unique partners
//   - 30-day sparkline: server-emitted daily buckets (always 30 entries,
//     today and the prior 29 days). Plain SVG line + area fill — no chart
//     library dep, keeps the chunk small.
//   - Recent feed: 50 most recent rows with partner + user embeds.
//
// Empty state shows when total === 0; the sparkline still renders a flat
// baseline so the layout doesn't shift between empty and populated states.
export function AssetViewsTab({ assetId }: { assetId: string }) {
  const query = useAssetViews(assetId);

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={Eye}
        title="Couldn't load views"
        description={query.error?.message ?? "Try refreshing."}
      />
    );
  }

  return <ViewsTabBody data={query.data} />;
}

function ViewsTabBody({ data }: { data: AssetViewAnalyticsResponse }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <HeroCard
          icon={Eye}
          label="Total views"
          value={data.total}
          hint="All time"
        />
        <HeroCard
          icon={Users}
          label="Unique partners"
          value={data.uniquePartners}
          hint="All time (excluding internal views)"
        />
      </div>

      <section className="rounded-xl border bg-card p-5">
        <header className="flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold tracking-tight">
            Last 30 days
          </h3>
          <span className="text-xs text-muted-foreground tabular-nums">
            {data.timeseries30d.reduce((sum, p) => sum + p.count, 0)} views
          </span>
        </header>
        <Sparkline points={data.timeseries30d} />
      </section>

      <section className="rounded-xl border bg-card">
        <header className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-display text-sm font-semibold tracking-tight">
            Recent views
          </h3>
          <span className="text-xs text-muted-foreground">
            Most recent {Math.min(50, data.recent.length)} shown
          </span>
        </header>
        {data.recent.length === 0 ? (
          <div className="px-5 py-8 text-center text-xs text-muted-foreground">
            No views yet. This asset hasn&apos;t been opened by anyone.
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {data.recent.map((v) => {
              const name = v.user?.full_name ?? "Unknown user";
              return (
                <li key={v.id} className="flex items-center gap-3 px-5 py-3">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-muted text-[10px] text-muted-foreground">
                      {initialsFromName(name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{name}</div>
                    <div className="text-xs text-muted-foreground">
                      {v.partner?.company_name ?? "—"}
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-muted-foreground">
                    <div>
                      {formatDate(v.viewed_at, {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div>{formatRelative(v.viewed_at)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function HeroCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Eye;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-semibold tabular-nums tracking-tight">
        {value.toLocaleString()}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

// Plain SVG sparkline — no chart library. 30 daily buckets in, area+line
// out. Y-axis auto-scales to max bucket value (or 1 for the empty case so
// the baseline doesn't flicker between renders).
function Sparkline({ points }: { points: Array<{ date: string; count: number }> }) {
  if (points.length === 0) return null;

  const width = 600;
  const height = 80;
  const padX = 4;
  const padY = 6;
  const maxCount = Math.max(1, ...points.map((p) => p.count));
  const stepX = (width - padX * 2) / Math.max(1, points.length - 1);

  const coords = points.map((p, i) => {
    const x = padX + i * stepX;
    const y = padY + (height - padY * 2) * (1 - p.count / maxCount);
    return { x, y, ...p };
  });

  const linePath = coords
    .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(
    1,
  )},${height - padY} L${coords[0].x.toFixed(1)},${height - padY} Z`;

  const firstDate = points[0].date;
  const lastDate = points[points.length - 1].date;

  return (
    <div className="mt-3">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-20 w-full text-accent"
        preserveAspectRatio="none"
        role="img"
        aria-label={`30-day views timeseries. Peak ${maxCount} per day.`}
      >
        <path d={areaPath} fill="currentColor" fillOpacity={0.12} />
        <path
          d={linePath}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {coords.map((c) => (
          <circle key={c.date} cx={c.x} cy={c.y} r={c.count > 0 ? 1.5 : 0} fill="currentColor" />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>{firstDate}</span>
        <span>peak {maxCount}/day</span>
        <span>{lastDate}</span>
      </div>
    </div>
  );
}
